/**
 * Componentes/constantes compartilhados pelas abas de Relatórios > Financeiro.
 * Segue o mesmo padrão visual de BillingHistoryView e do módulo Financeiro.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, CreditCard, Landmark, Minus } from 'lucide-react';
import { companyService } from '@services/companyService';
import { formatCnpj, type Company } from '@/types/company';
import type { ExpenseTransactionSource } from '@/types/financialReports';
import { formatCurrencyBRL } from '@pages/financial/financialUtils';
import { selectCls } from '@pages/financial/financialShared';
import { competenceLabelPtBR, currentCompetence, formatPct, isFutureCompetence } from './financialReportsUtils';
import type { ReportBasis } from '@/types/financialReports';

// ─── Filtros de período ─────────────────────────────────────────

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/**
 * Opções de mês. Sem `markFutureFor` o resultado é idêntico ao de sempre
 * (`isFuture` sempre falso, `label` inalterada) — é o que protege os
 * consumidores fora do escopo desta tela, como o Fluxo de Caixa.
 *
 * Com o ano informado, os meses posteriores à competência corrente são
 * marcados, para que o usuário não escolha um período futuro sem perceber.
 */
export const getMonthOptions = (
  markFutureFor?: { year: number; today?: Date },
): { value: number; label: string; isFuture: boolean }[] => {
  const ref = markFutureFor ? currentCompetence(markFutureFor.today) : null;
  return MONTHS.map((label, i) => ({
    value: i + 1,
    label,
    isFuture: ref != null && markFutureFor != null && isFutureCompetence(markFutureFor.year, i + 1, ref),
  }));
};

// ─── Realizado × Projetado ──────────────────────────────────────

/**
 * Alterna entre o realizado e os lançamentos já registrados para uma
 * competência futura. Mesmo visual do sub-toggle de Fluxo de Caixa, e a mesma
 * palavra ("Projetado") — duas palavras diferentes para a mesma ideia na mesma
 * tela custariam mais do que ganhariam.
 */
export const BasisToggle: React.FC<{
  value: ReportBasis;
  onChange: (basis: ReportBasis) => void;
}> = ({ value, onChange }) => (
  <div className="inline-flex w-full sm:w-auto rounded-lg border border-slate-300 overflow-hidden">
    {(['realizado', 'projetado'] as ReportBasis[]).map(b => (
      <button
        key={b}
        type="button"
        onClick={() => onChange(b)}
        className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium transition-colors ${
          value === b ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
        }`}
      >
        {b === 'realizado' ? 'Realizado' : 'Projetado'}
      </button>
    ))}
  </div>
);

/** Faixa permanente enquanto o modo Projetado estiver ligado. */
export const ProjectedBanner: React.FC = () => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
    <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
    <p className="text-sm text-amber-800">
      <span className="font-semibold">Modo Projetado.</span> Estes são lançamentos já registrados
      (recorrências, faturas de cartão e lançamentos avulsos) para uma competência que ainda não
      ocorreu — não é realizado. A receita não é projetada: ela vem de notas fiscais emitidas.
    </p>
  </div>
);

/**
 * Estado vazio de uma competência futura em modo Realizado. Substitui o
 * "Nenhuma despesa encontrada no período", que não dizia o motivo, e oferece o
 * caminho: ver o que já está lançado. A troca é sempre explícita — nunca
 * alternamos o modo por conta própria.
 */
export const FutureCompetenceNotice: React.FC<{
  year: number;
  month: number | null;
  onSwitchToProjected: () => void;
}> = ({ year, month, onSwitchToProjected }) => (
  <div className="py-8 text-center space-y-3">
    <p className="text-sm text-slate-600">
      <span className="font-medium text-slate-800">Competência futura.</span> Este relatório mostra
      apenas o realizado.
    </p>
    <button
      type="button"
      onClick={onSwitchToProjected}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
    >
      Ver o projetado
      {month != null ? ` de ${competenceLabelPtBR(year, month)}` : ` de ${year}`}
    </button>
    <p className="text-xs text-slate-500">
      Mostra os lançamentos já registrados para o período.
    </p>
  </div>
);

export const getYearOptions = (): number[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear - 3; y <= currentYear + 1; y++) years.push(y);
  return years;
};

/** Abreviação em milhares do eixo Y dos gráficos (padrão BillingHistoryView). */
export const kFormatter = (v: number): string => `${(v / 1000).toFixed(0)}k`;

// ─── Empresas (Consolidado default) ─────────────────────────────

/** Carrega a lista de empresas uma vez (para o seletor de empresa). */
export const useCompanies = (): { companies: Company[]; companiesError: string | null } => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  useEffect(() => {
    companyService
      .getCompanies()
      .then(setCompanies)
      .catch((err) => setCompaniesError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
  }, []);

  return { companies, companiesError };
};

/** Select de empresa com "Consolidado (todas)" como opção default (value null). */
export const CompanySelect: React.FC<{
  companies: Company[];
  value: number | null;
  onChange: (companyId: number | null) => void;
  className?: string;
}> = ({ companies, value, onChange, className }) => (
  <select
    className={className ?? `${selectCls} w-full sm:w-56`}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
  >
    <option value="">Consolidado (todas)</option>
    {companies.map((c) => (
      <option key={c.company_id} value={c.company_id}>
        {formatCnpj(c.document)} — {c.name}
      </option>
    ))}
  </select>
);

// ─── Badge de fonte do lançamento (Conta / Cartão) ──────────────

/** Badge da origem do lançamento no drill-down (conta corrente ou cartão). */
export const SourceBadge: React.FC<{ source: ExpenseTransactionSource }> = ({ source }) =>
  source === 'cartao' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
      <CreditCard size={12} /> Cartão
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <Landmark size={12} /> Conta
    </span>
  );

// ─── Badge de variação percentual ───────────────────────────────

/**
 * Badge de variação % (MoM/YoY). `positiveIsGood` inverte as cores para
 * métricas em que crescer é ruim (despesas).
 */
export const DeltaBadge: React.FC<{ pct: number | null; positiveIsGood?: boolean }> = ({
  pct,
  positiveIsGood = true,
}) => {
  if (pct === null) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
        <Minus size={12} /> —
      </span>
    );
  }
  const up = pct > 0;
  const good = up === positiveIsGood || pct === 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        good ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {formatPct(Math.abs(pct))}
    </span>
  );
};

// ─── Tooltip de gráfico (card branco + moeda pt-BR) ─────────────

/**
 * Tooltip padrão dos gráficos (padrão BillingHistoryView).
 * Com `showTotal`, exibe uma linha adicional com a soma dos valores do payload —
 * usado nas barras empilhadas para mostrar o total consolidado (ex.: total do dia)
 * além do valor de cada série/canal. `totalLabel` customiza o rótulo (padrão "Total").
 */
export const ChartTooltip: React.FC<any> = ({ active, payload, label, valueFormatter, showTotal, totalLabel }) => {
  if (!active || !payload?.length) return null;
  const fmt: (v: number) => string = valueFormatter ?? formatCurrencyBRL;
  const total = showTotal
    ? payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0)
    : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((entry: any, idx: number) => (
        <p key={idx} style={{ color: entry.color }} className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
          {entry.name}: {fmt(entry.value)}
        </p>
      ))}
      {showTotal && (
        <p className="flex items-center gap-1 mt-1 pt-1 border-t border-slate-200 font-semibold text-slate-700">
          {totalLabel ?? 'Total'}: {fmt(total)}
        </p>
      )}
    </div>
  );
};

// ─── Paleta das séries de gráficos ──────────────────────────────

/** Paleta para séries múltiplas (evolução 12 meses, linhas diárias, etc.). */
export const SERIES_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#64748b', // slate
];
